import { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, FormControl, IconButton, InputLabel, List, ListItem,
  ListItemSecondaryAction, ListItemText, MenuItem, Select, Tooltip, Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useAssignLabMutation, useRemoveLabAssignmentMutation } from '../hooks/useUserMutations';
import { useLabsQuery } from '../../labs/hooks/useLabsQuery';
import type { UserLabAssignment } from '../types/user.types';

interface UserLabsSectionProps {
  userId: string;
  currentLabs: UserLabAssignment[];
}

export function UserLabsSection({ userId, currentLabs }: UserLabsSectionProps) {
  const [removeTarget, setRemoveTarget] = useState<UserLabAssignment | null>(null);
  const [selectedLabId, setSelectedLabId] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);

  const { data: allLabs } = useLabsQuery();
  const assignMutation = useAssignLabMutation(userId);
  const removeMutation = useRemoveLabAssignmentMutation(userId);

  const assignedLabIds = new Set(currentLabs.map((l) => l.labId));
  const availableLabs = allLabs?.filter(
    (l) => l.status === 'active' && !assignedLabIds.has(l.id)
  ) ?? [];

  const handleAssign = async () => {
    if (!selectedLabId) return;
    await assignMutation.mutateAsync(selectedLabId);
    setSelectedLabId('');
    setAssignOpen(false);
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    await removeMutation.mutateAsync(removeTarget.labId);
    setRemoveTarget(null);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2">Lab Assignments</Typography>
        <Button
          size="small"
          onClick={() => setAssignOpen(true)}
          disabled={availableLabs.length === 0}
        >
          Assign Lab
        </Button>
      </Box>

      <List dense disablePadding>
        {currentLabs.map((lab) => (
          <ListItem key={lab.labId} divider>
            <ListItemText
              primary={lab.labName}
              secondary={lab.roleInLab ?? undefined}
            />
            <ListItemSecondaryAction>
              <Tooltip title={currentLabs.length <= 1 ? 'User must have at least one lab' : 'Remove'}>
                <span>
                  <IconButton
                    edge="end"
                    size="small"
                    disabled={currentLabs.length <= 1}
                    onClick={() => setRemoveTarget(lab)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {currentLabs.length === 0 && (
          <ListItem>
            <ListItemText secondary="No labs assigned" />
          </ListItem>
        )}
      </List>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Lab</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Select Lab</InputLabel>
            <Select
              value={selectedLabId}
              label="Select Lab"
              onChange={(e) => setSelectedLabId(e.target.value)}
            >
              {availableLabs.map((lab) => (
                <MenuItem key={lab.id} value={lab.id}>{lab.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!selectedLabId || assignMutation.isPending}
          >
            {assignMutation.isPending ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove confirmation */}
      <Dialog open={Boolean(removeTarget)} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Remove Lab Assignment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove <strong>{removeTarget?.labName}</strong> from this user?
            The user will lose access to this lab's data at their next login.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRemove}
            disabled={removeMutation.isPending}
          >
            {removeMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
